import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AlertTriangle, Clock, Calendar, ClipboardCheck, ArrowRight } from "lucide-react";

/*
 * MonitoringWidget , compact 30/60/90 summary drop-in for HR + Admin dashboards.
 * One line per stat (overdue / due today / upcoming 7d) with jump-to-full-page CTA.
 * Safely no-ops if the caller is not authorised (endpoint returns 403).
 */
export default function MonitoringWidget() {
  const [counts, setCounts] = useState({ overdue: 0, due: 0, upcoming: 0, scheduled: 0 });
  const [firstOverdue, setFirstOverdue] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/monitoring/upcoming")
      .then((r) => {
        if (!alive) return;
        setCounts(r.data.counts || { overdue: 0, due: 0, upcoming: 0, scheduled: 0 });
        const list = r.data.items || [];
        setFirstOverdue(list.find((it) => it.status === "overdue") || list.find((it) => it.status === "due") || null);
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const actionable = counts.overdue + counts.due;
  const banner = counts.overdue > 0
    ? "border-red-300 bg-red-50/60"
    : (counts.due > 0 ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-white");

  return (
    <Link
      to="/monitoring"
      className={"data-cell p-5 transition-colors hover:border-blue-600 group " + banner}
      data-testid="monitoring-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={16} className="text-slate-800" strokeWidth={1.75} />
          <span className="eyebrow">30/60/90 monitoring</span>
        </div>
        <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-all" />
      </div>

      {!loaded ? (
        <div className="text-xs text-slate-500 font-mono">loading…</div>
      ) : (
        <>
          <div className="font-display text-3xl font-semibold text-slate-900 leading-none">
            {actionable}
          </div>
          <div className="text-xs text-slate-500 mt-1 font-mono">
            {actionable === 0 ? "All check-ins current" : `${actionable} need${actionable === 1 ? "s" : ""} action`}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1 text-[10px] font-mono">
            <Cell Icon={AlertTriangle} tone="text-red-700 bg-red-50 border-red-200" label="OVERDUE" value={counts.overdue} testid="widget-overdue" />
            <Cell Icon={Clock} tone="text-amber-800 bg-amber-50 border-amber-300" label="DUE" value={counts.due} testid="widget-due" />
            <Cell Icon={Calendar} tone="text-blue-700 bg-blue-50 border-blue-200" label="7-DAY" value={counts.upcoming} testid="widget-upcoming" />
          </div>
          {firstOverdue && (
            <div className="mt-3 text-[11px] text-slate-600 truncate" data-testid="widget-first-overdue">
              <span className="font-semibold text-slate-800">{firstOverdue.case_number}</span>
              <span className="mx-1 text-slate-400">·</span>
              {firstOverdue.days}-day
              <span className="mx-1 text-slate-400">·</span>
              due {new Date(firstOverdue.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </div>
          )}
        </>
      )}
    </Link>
  );
}

function Cell({ Icon, tone, label, value, testid }) {
  return (
    <div className={"border px-2 py-1.5 rounded flex items-center justify-between " + tone} data-testid={testid}>
      <div className="flex items-center gap-1">
        <Icon size={9} strokeWidth={2.25} />
        <span className="tracking-widest">{label}</span>
      </div>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
